import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import { formatCurrency, formatMinutes, formatTurkishDate } from '../helpers';

/**
 * Verilen sipariş verilerini IndentedTree görselleştirmesi için dönüştürür.
 */
function buildHierarchy(data) {
    // Kök düğüm
    const root = {
        name: 'Toplam Sipariş',
        children: [],
    };

    // Her sipariş için
    data.forEach((item) => {
        const stokHareketi = item.stokHareketi || item;
        const stokAdi = stokHareketi?.KartAdi || stokHareketi?.StokAdi || 'Bilinmeyen Stok';
        const siparisNo = stokHareketi?.SiparisNo || '';
        const stokTutar = stokHareketi?.Tutar || 0;

        // Dış katman - Sipariş düğümü
        const siparisNode = {
            name: stokAdi,
            siparisNo: siparisNo,
            tutar: stokTutar,
            children: [],
        };

        // İş emirleri varsa ekle
        if (item.isEmri) {
            // İş emrinin kendisi
            processIsEmriNode(item.isEmri, siparisNode);
        }

        root.children.push(siparisNode);
    });

    return root;
}

/**
 * İş emri bilgilerini işler ve ağaç yapısına ekler
 */
function processIsEmriNode(isEmri, parentNode) {
    // İş emri detayları
    const receteAdi = isEmri.isEmri?.[0]?.ReceteAdi || 'Bilinmeyen Reçete';
    const isEmriNo = isEmri.IsEmriNo || isEmri.isEmri?.[0]?.IsEmriNo || '';
    const isEmriID = isEmri.IsEmriID || isEmri.isEmri?.[0]?.IsEmriID || '';
    const cariAdi = isEmri.CariAdi || isEmri.isEmri?.[0]?.CariAdi || '';
    const birimAdi = isEmri.BirimAdi || isEmri.isEmri?.[0]?.BirimAdi || '';
    const miktar = isEmri.Miktar || isEmri.isEmri?.[0]?.Miktar || 0;
    const siparisNo = isEmri.SiparisNo || isEmri.isEmri?.[0]?.SiparisNo || '';
    const tarih = isEmri.Tarih || isEmri.isEmri?.[0]?.Tarih || '';
    const teslimTarihi = isEmri.TeslimTarihi || isEmri.isEmri?.[0]?.TeslimTarihi || '';
    const kapali = isEmri.Kapali || isEmri.isEmri?.[0]?.Kapali || false;
    const aciklama = isEmri.Aciklama || isEmri.isEmri?.[0]?.Aciklama || '';
    // Tutar bilgisini direkt olarak isEmri'den veya isEmri.isEmri'den al
    const tutar =
        isEmri.Tutar !== undefined
            ? isEmri.Tutar
            : isEmri.isEmri && isEmri.isEmri[0] && isEmri.isEmri[0].Tutar !== undefined
            ? isEmri.isEmri[0].Tutar
            : 0;

    // İş emri düğümü
    const isEmriNode = {
        name: receteAdi,
        isEmriNo: isEmriNo,
        isEmriID: isEmriID,
        cariAdi: cariAdi,
        birimAdi: birimAdi,
        miktar: miktar,
        siparisNo: siparisNo,
        tarih: tarih,
        teslimTarihi: teslimTarihi,
        kapali: kapali,
        aciklama: aciklama,
        tutar: tutar,
        children: [],
    };

    // İç katman - Üretim planları
    if (Array.isArray(isEmri.uretimPlanlari) && isEmri.uretimPlanlari.length > 0) {
        isEmri.uretimPlanlari.forEach((plan) => {
            const operasyonAdi = plan.OperasyonAdi || 'Bilinmeyen Operasyon';
            // Miktar ve süre değerleri
            const miktar = plan.MiktarToplam || plan.Miktar || 0;
            const sureToplam = plan.SureToplam || 0;
            const birim = plan.BirimAdi || 'Ad';

            // Üretim planı düğümü
            const planNode = {
                name: operasyonAdi,
                miktar: miktar,
                birim: birim,
                sureToplam: sureToplam,
                sureKalan: plan.SureKalan || 0,
                miktarKalan: plan.MiktarKalan || 0,
                isEmriNo: plan.IsEmriNo || isEmriNo,
                isEmriID: plan.IsEmriID || isEmriID,
                makineAdi: plan.MakineAdi || '',
                makineKodu: plan.MakineKodu || '',
                operasyonKodu: plan.OperasyonKodu || '',
                operasyonSirasi: plan.OperasyonSirasi || 0,
                tarih: plan.Tarih || '',
                kapali: plan.Kapali || false,
            };

            isEmriNode.children.push(planNode);
        });
    }

    // Alt iş emirleri varsa, onları da işle
    if (Array.isArray(isEmri.altIsEmirleri) && isEmri.altIsEmirleri.length > 0) {
        isEmri.altIsEmirleri.forEach((altIsEmri) => {
            // Alt iş emirlerine üst iş emirinden eksik özellikleri aktaralım
            const enrichedAltIsEmri = {
                ...altIsEmri,
                // Eksik bilgileri üst iş emrinden al
                Aciklama: altIsEmri.Aciklama || aciklama,
                CariAdi: altIsEmri.CariAdi || cariAdi,
                BirimAdi: altIsEmri.BirimAdi || birimAdi,
                SiparisNo: altIsEmri.SiparisNo || siparisNo,
                Tarih: altIsEmri.Tarih || tarih,
                TeslimTarihi: altIsEmri.TeslimTarihi || teslimTarihi,
            };
            processIsEmriNode(enrichedAltIsEmri, isEmriNode);
        });
    }

    parentNode.children.push(isEmriNode);
}

/**
 * Metin kesmek için yardımcı fonksiyon
 */
function truncateText(text, maxLength) {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength - 3) + '...' : text;
}

/**
 * Miktar bilgisini formatlar
 */
function formatMiktar(miktar, birim = 'Ad') {
    if (miktar === undefined || miktar === null) return '-';
    return `${miktar.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ${birim}`;
}

/**
 * Girintili ağaç görselleştirmesi
 */
function IndentedTree({ data, height = '100%', theme }) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!data || !containerRef.current) return;

        // Eski görselleştirmeyi temizle
        d3.select(containerRef.current).selectAll('*').remove();

        try {
            // Verinin hiyerarşik formata dönüştürülmesi
            const hierarchyData = buildHierarchy(data);

            // Root container boyutları
            const container = containerRef.current;
            const containerWidth = container.clientWidth || 1200;

            // Supabase teması için light gray ve green renk paleti
            const defaultTheme = {
                colors: {
                    brand: '#24B47E', // Primary green
                    secondary: '#3ECF8E', // Light green
                    accent: '#6F76D9', // Accent purple
                    danger: '#F76631', // Danger/warning color
                    background: {
                        light: '#F8FAFC', // Light background
                        default: '#F1F5F9', // Default background
                        dark: '#E2E8F0', // Darker background/borders
                    },
                    text: {
                        primary: '#1E293B', // Primary text
                        secondary: '#64748B', // Secondary text
                        light: '#94A3B8', // Light text
                    },
                    status: {
                        success: '#3ECF8E', // Success
                        warning: '#F2C94C', // Warning
                        error: '#F76631', // Error
                    },
                },
                fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
            };

            // Kullanıcı tarafından sağlanan tema değerlerini veya varsayılanları kullan
            const colors = theme?.colors || defaultTheme.colors;
            const fontFamily = theme?.fontFamily || defaultTheme.fontFamily;

            // İyileştirilmiş stil parametreleri
            const nodeHeight = 40; // Düğümler arası dikey mesafe - artırıldı
            const nodeSize = 22; // Düğüm genişlik birimi - ayarlandı
            const iconSize = 5; // Çember yarıçapı - artırıldı
            const fontSize = '13px'; // Ana font boyutu - artırıldı
            const indentSize = 40; // Girinti miktarı - özelleştirildi

            // D3 hiyerarşisi oluştur ve her düğüme indeks ata
            const root = d3.hierarchy(hierarchyData).eachBefore(
                (
                    (i) => (d) =>
                        (d.index = i++)
                )(0)
            );

            const nodes = root.descendants();

            // SVG yüksekliğini düğüm sayısına göre ayarla (minimum 500px)
            const calculatedHeight = Math.max((nodes.length + 1) * nodeHeight, 500);

            // İçerik genişliği
            const width = containerWidth;
            const height = calculatedHeight;

            // Detay sütunlar (sağdaki bilgiler)
            const columns = [
                {
                    label: 'Miktar',
                    value: (d) => {
                        if (d.depth <= 1) return ''; // Ana kök ve sipariş düğümleri için boş
                        if (d.depth === 2) return formatMiktar(d.data.miktar, d.data.birimAdi); // İş emri
                        if (d.depth >= 3) return formatMiktar(d.data.miktar, d.data.birim); // Üretim planı
                        return '';
                    },
                    x: 380,
                },
                {
                    label: 'Süre',
                    value: (d) => {
                        if (d.depth >= 3 && d.data.sureToplam) return formatMinutes(d.data.sureToplam);
                        return '';
                    },
                    x: 480,
                },
                {
                    label: 'Tarih',
                    value: (d) => {
                        if ((d.depth === 2 || d.depth >= 3) && d.data.tarih)
                            return formatTurkishDate(d.data.tarih, true);
                        return '';
                    },
                    x: 580,
                },
                {
                    label: 'Makine',
                    value: (d) => (d.depth >= 3 && d.data.makineAdi ? d.data.makineAdi : ''),
                    x: 700,
                },
                {
                    label: 'Durum',
                    value: (d) => (d.depth >= 3 ? (d.data.kapali ? 'Tamamlandı' : 'Devam Ediyor') : ''),
                    color: (d) =>
                        d.depth >= 3
                            ? d.data.kapali
                                ? colors.status.success
                                : colors.status.warning
                            : colors.text.primary,
                    x: 820,
                },
            ];

            // SVG oluştur
            const svg = d3
                .select(containerRef.current)
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .attr('viewBox', [-nodeSize / 2, -nodeHeight * 1.2, width, height])
                .attr('style', `max-width: 100%; height: auto; font: ${fontSize} ${fontFamily}; overflow: visible;`);

            // Bağlantı çizgileri - daha belirgin
            const link = svg
                .append('g')
                .attr('fill', 'none')
                .attr('stroke', colors.text.light)
                .selectAll()
                .data(root.links())
                .join('path')
                .attr('stroke-opacity', 0.6)
                .attr('stroke-width', 1.2)
                .attr(
                    'd',
                    (d) => `
                    M${d.source.depth * indentSize},${d.source.index * nodeHeight}
                    V${d.target.index * nodeHeight}
                    h${indentSize}
                `
                );

            // Düğüm grupları
            const node = svg
                .append('g')
                .selectAll()
                .data(nodes)
                .join('g')
                .attr('transform', (d) => `translate(0,${d.index * nodeHeight})`);

            // Düğüm çemberleri
            node.append('circle')
                .attr('cx', (d) => d.depth * indentSize)
                .attr('r', iconSize)
                .attr('fill', (d) => {
                    // Kök düğüm
                    if (d.depth === 0) return colors.text.light;
                    // Sipariş düğümleri (depth 1)
                    if (d.depth === 1) return colors.danger;
                    // İş emri düğümleri (depth 2)
                    if (d.depth === 2) return colors.brand;
                    // Üretim planı düğümleri (depth 3+)
                    if (d.depth >= 3) return colors.accent;
                    return colors.text.light; // varsayılan
                })
                .attr('stroke', (d) => {
                    if (d.depth === 0) return 'none';
                    if (d.depth === 1) return d3.color(colors.danger).darker(0.2);
                    if (d.depth === 2) return d3.color(colors.brand).darker(0.2);
                    if (d.depth >= 3) return d3.color(colors.accent).darker(0.2);
                    return 'none';
                })
                .attr('stroke-width', 0.5);

            // Arka plan dikdörtgenleri (daha iyi okunabilirlik için)
            node.filter((d) => d.depth > 0) // Kök hariç
                .append('rect')
                .attr('x', (d) => d.depth * indentSize + nodeSize / 2)
                .attr('y', -nodeHeight / 2 + 6)
                .attr('width', (d) => {
                    // Sütun pozisyonuna göre en az genişlik
                    const minWidth =
                        Math.max(...columns.map((col) => col.x - (d.depth * indentSize + nodeSize / 2))) - 30;
                    return Math.max(minWidth, 180);
                })
                .attr('height', nodeHeight - 12)
                .attr('rx', 4)
                .attr('ry', 4)
                .attr('fill', colors.background.light)
                .attr('fill-opacity', 0.9)
                .attr('stroke', (d) => {
                    if (d.depth === 1) return colors.danger;
                    if (d.depth === 2) return colors.brand;
                    if (d.depth >= 3) return colors.accent;
                    return colors.text.light;
                })
                .attr('stroke-width', 1)
                .attr('stroke-opacity', 0.4);

            // Düğüm isimleri - daha iyi konumlandırma
            node.append('text')
                .attr('dy', '0.32em')
                .attr('x', (d) => d.depth * indentSize + nodeSize)
                .text((d) => {
                    // Derinliğe göre gösterilecek bilgi farklılaştır
                    if (d.depth === 0) return truncateText(d.data.name, 20); // Root düğüm
                    if (d.depth === 1) return truncateText(d.data.name, 25); // Stok hareketi
                    if (d.depth === 2) return truncateText(`${d.data.name} (${d.data.isEmriNo})`, 30); // İş emri
                    if (d.depth >= 3) {
                        // Üretim planı
                        return truncateText(d.data.name, 25);
                    }
                    return truncateText(d.data.name, 20);
                })
                .attr('fill', colors.text.primary)
                .attr('font-weight', (d) => (d.depth <= 1 ? '500' : 'normal'))
                .attr('text-rendering', 'optimizeLegibility');

            // Düğüm açıklamaları (hover)
            node.append('title').text((d) => {
                if (d.depth === 0) return d.data.name;
                if (d.depth === 1) return `${d.data.name}\nSipariş No: ${d.data.siparisNo || '-'}`;
                if (d.depth === 2)
                    return `${d.data.name}\nİş Emri No: ${d.data.isEmriNo}\nMiktar: ${formatMiktar(
                        d.data.miktar,
                        d.data.birimAdi
                    )}\nTutar: ${formatCurrency(d.data.tutar)}`;
                if (d.depth >= 3)
                    return `${d.data.name}\nMakine: ${d.data.makineAdi || '-'}\nMiktar: ${formatMiktar(
                        d.data.miktar,
                        d.data.birim
                    )}\nSüre: ${formatMinutes(d.data.sureToplam || 0)}`;
                return d.data.name;
            });

            // Sütun başlıkları - daha belirgin
            for (const { label, value, x, color } of columns) {
                svg.append('text')
                    .attr('dy', '0.32em')
                    .attr('y', -nodeHeight * 0.7)
                    .attr('x', x)
                    .attr('text-anchor', 'middle')
                    .attr('font-weight', '600')
                    .attr('font-size', '13px')
                    .attr('fill', colors.text.secondary)
                    .text(label);

                // Sütun değerleri - daha belirgin
                node.append('text')
                    .attr('dy', '0.32em')
                    .attr('x', x)
                    .attr('text-anchor', 'middle')
                    .attr('fill', (d) => (color ? color(d) : colors.text.primary))
                    .attr('font-weight', (d) => (label === 'Durum' ? '600' : 'normal'))
                    .attr('font-size', '12px')
                    .text((d) => value(d));
            }

            // Sipariş No bilgisi (Depth = 1 için) - daha iyi konumlama
            node.filter((d) => d.depth === 1 && d.data.siparisNo)
                .append('text')
                .attr('dy', '0.32em')
                .attr('x', (d) => columns[0].x - 140)
                .attr('text-anchor', 'end')
                .attr('fill', colors.text.secondary)
                .attr('font-size', '12px')
                .text((d) => `Sipariş No: ${d.data.siparisNo}`);

            // Tutar bilgisi (Depth = 2 için) - daha iyi konumlama
            node.filter((d) => d.depth === 2 && d.data.tutar)
                .append('text')
                .attr('dy', '0.32em')
                .attr('x', (d) => columns[0].x - 120)
                .attr('text-anchor', 'end')
                .attr('fill', colors.danger)
                .attr('font-size', '12px')
                .text((d) => `Tutar: ${formatCurrency(d.data.tutar)}`);

            // Özet bilgi panosu - Tamamlanan ve devam eden planların sayısı
            const summary = {
                total: nodes.filter((d) => d.depth >= 3).length,
                completed: nodes.filter((d) => d.depth >= 3 && d.data.kapali).length,
                inProgress: nodes.filter((d) => d.depth >= 3 && !d.data.kapali).length,
            };

            // Özet bilgi panosu ekle - Daha belirgin
            const summaryPanel = svg.append('g').attr('transform', `translate(${width - 230}, 15)`);

            // Arka plan - Daha belirgin
            summaryPanel
                .append('rect')
                .attr('width', 210)
                .attr('height', 90)
                .attr('rx', 6)
                .attr('ry', 6)
                .attr('fill', colors.background.light)
                .attr('stroke', colors.background.dark)
                .attr('stroke-width', 1.5)
                .attr('stroke-opacity', 0.6);

            // Başlık - Daha belirgin
            summaryPanel
                .append('text')
                .attr('x', 105)
                .attr('y', 25)
                .attr('text-anchor', 'middle')
                .attr('font-weight', '600')
                .attr('font-size', '14px')
                .attr('fill', colors.text.primary)
                .text('Özet Bilgiler');

            // Toplam plan - Daha belirgin
            summaryPanel
                .append('text')
                .attr('x', 25)
                .attr('y', 50)
                .attr('fill', colors.text.primary)
                .attr('font-size', '13px')
                .attr('font-weight', '500')
                .text(`Toplam Plan: ${summary.total}`);

            // Tamamlanan plan - Daha belirgin
            summaryPanel
                .append('text')
                .attr('x', 25)
                .attr('y', 72)
                .attr('fill', colors.status.success)
                .attr('font-size', '13px')
                .attr('font-weight', '500')
                .text(`Tamamlanan: ${summary.completed}`);

            // Devam eden plan - Daha belirgin
            summaryPanel
                .append('text')
                .attr('x', 130)
                .attr('y', 72)
                .attr('fill', colors.status.warning)
                .attr('font-size', '13px')
                .attr('font-weight', '500')
                .text(`Devam Eden: ${summary.inProgress}`);

            // Çizgi ekle - Başlık ve içerik arası
            summaryPanel
                .append('line')
                .attr('x1', 20)
                .attr('y1', 32)
                .attr('x2', 190)
                .attr('y2', 32)
                .attr('stroke', colors.background.dark)
                .attr('stroke-width', 1)
                .attr('stroke-opacity', 0.5);

            // Zoom işlevi
            const zoom = d3
                .zoom()
                .scaleExtent([0.1, 3])
                .on('zoom', (event) => {
                    svg.selectAll('g').attr('transform', event.transform);
                });

            svg.call(zoom);

            // Grafiği ekran boyutuna sığdırmak için başlangıçta hafif zoom out
            const initialScale = Math.min(1, (containerWidth / width) * 0.9);
            svg.call(zoom.transform, d3.zoomIdentity.translate(20, 20).scale(initialScale));
        } catch (error) {
            console.error('IndentedTree hata:', error, error.stack);
            // Fallback color for error messages
            const errorColor = '#F76631';
            // Hata durumunda basit bir mesaj göster
            d3.select(containerRef.current)
                .append('div')
                .style('color', errorColor)
                .style('padding', '20px')
                .style('text-align', 'center')
                .text('Görselleştirme oluşturulurken bir hata oluştu: ' + error.message);
        }
    }, [data, theme]);

    // Theme properties or defaults
    const bgColor = theme?.colors?.background?.default || '#F1F5F9';
    const textColor = theme?.colors?.text?.primary || '#1E293B';
    const fontFamily = theme?.fontFamily || "'Inter', -apple-system, system-ui, sans-serif";

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: height,
                position: 'relative',
                backgroundColor: bgColor,
                color: textColor,
                borderRadius: '6px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '15px',
                overflow: 'hidden',
                fontFamily: fontFamily,
            }}
        />
    );
}

IndentedTree.propTypes = {
    data: PropTypes.array.isRequired,
    height: PropTypes.string,
    theme: PropTypes.object,
};

export default IndentedTree;
